import React from "react";
import { FormControl, MenuItem, Select, FormHelperText, Typography, Box, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { autoBatchEnhancer } from "@reduxjs/toolkit";
import { IoIosLock } from "react-icons/io";

const SelectDropdown = ({
  name,
  value,
  label,
  options = [],
  onChange,
  onClear,
  onBlur,
  error,
  required = false,
  touched,
  imageSrc,
  helperText = "",
  containerStyles = {},
  disabled = false,
  direction = "ltr",
  t,
  renderIcon, // New prop for rendering icons
  ...props
}) => {
  const isError = touched && Boolean(error);

  // Helper function to find the selected option's name based on value
  const getSelectedOptionName = () => {
    const selectedOption = options.find(option => option.id === value);
    return selectedOption ? selectedOption.name : "";
  };

  // Helper function to find the selected option's icon based on value
  const getSelectedOptionIcon = () => {
    const selectedOption = options.find(option => option.id === value);
    return selectedOption && renderIcon ? renderIcon(selectedOption) : null;
  };

  return (
    <FormControl
      fullWidth
      margin="normal"
      error={isError}
      disabled={disabled}
      sx={{ direction }}
      {...props}
    >
      <Box sx={{ display: "flex", alignItems: "center", textAlign: "center", position: "relative" }}>
        {/* Typography for label with image */}
        <Typography
          sx={{
            display: "flex",
            alignItems: "center",
            marginBottom: "8px",
            fontSize: "16px",
            fontWeight: 500,
            color: "#676464",
            lineHeight: "16.41px",
          }}
        >
          {imageSrc && (
            <img
              src={imageSrc}
              alt={label}
               loading="lazy"
              style={{
                marginRight: "8px",
                marginLeft: direction ? "8px" : "0",
                width: "16px",
                height: "16px",
              }}
            />
          )}
          {label}
        </Typography>
        {required && (
          <Typography
            sx={{
              color: "#CA0B0B",
              fontWeight: 700,
              mt: "-18px",
              ml: "3px",
              fontSize: "20px",
            }}
          >
            *
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
        <Select
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          displayEmpty
          fullWidth
          renderValue={(selected) =>
            selected ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {getSelectedOptionIcon()}
                {getSelectedOptionName()}
              </Box>
            ) : (
              <span
                style={{
                  color: "#AFACAC",
                  fontSize: "14px",
                }}
              >{`${t.select}  ${label}`}</span>
            )
          }
          inputProps={{
            id: name,
          }}
          sx={{
            borderRadius: "10px",
            direction,
            pr: direction === "rtl" ? 2 : 0,
            borderColor: isError ? "#CA0B0B" : "#ccc",
            color: "#12538B",
            "&:hover": {
              borderColor: isError ? "#CA0B0B" : "#4e4e4e",
            },
            "&.Mui-focused": {
              borderColor: isError ? "#CA0B0B" : "#12538B",
            },
          }}
          MenuProps={{
            disableScrollLock: true,
            PaperProps: {
              style: {
                maxHeight: 200,
                overflowY: "auto",
                position: "absolute",
                top: "100%",
                zIndex: 9999,
              },
            },
            PopperProps: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 8],
                  },
                },
                {
                  name: "preventOverflow",
                  options: {
                    boundary: "viewport",
                  },
                },
                {
                  name: "flip",
                  enabled: true,
                },
                {
                  name: "arrow",
                  enabled: false,
                },
              ],
            },
          }}
        >
          {/* Handle the case where options are empty */}
          {options.length > 0 && (
            <MenuItem
              value=""
              disabled
              sx={{
                direction: direction === "rtl" ? "rtl !important" : "ltr !important",
              }}
            >
              <span>{`${t.select} ${label}`}</span>
            </MenuItem>
          )}
          {options.length > 0 ? (
            options.map((option, index) => (
              <MenuItem
                key={index}
                value={option.id}
                sx={{
                  color: "#12538B",
                  direction: direction === "rtl" ? "rtl !important" : "ltr !important",
                }}
              >
                {renderIcon && renderIcon(option)} {option.name}
              </MenuItem>
            ))
          ) : (
            <MenuItem
              value=""
              disabled
              sx={{
                direction: direction === "rtl" ? "rtl !important" : "ltr !important",
              }}
              style={{
                fontSize: "14px",
              }}
            >
              <span>{helperText || t.noOptions}</span>
            </MenuItem>
          )}
        </Select>
        
        {/* Close icon for clearing selection */}
        {name === "unit" && value && (
          <IconButton
            size="small"
            onClick={onClear}
            sx={{
              position: "absolute",
              left: direction === "rtl" ? 8:"auto",
              right: direction === "rtl" ? "auto": 8, 
              color: "#676464",
            }}
          >
            <ClearIcon />
          </IconButton>
        )}
          {name === "unit" && disabled && (
          <IconButton
            size="small"
           
            sx={{
              position: "absolute",
              left: direction === "rtl" ? 8:"auto",
              right: direction === "rtl" ? "auto": 8, 
              color: "#676464",
            }}
          >
            <IoIosLock />
          </IconButton>
        )}
      </Box>

      {/* Show error helper text */}
      {isError && <FormHelperText error  sx={{
      textAlign: direction === "rtl" ? "start" : "left",
    }}>{error}</FormHelperText>}
    </FormControl>
  );
};

export default SelectDropdown;
